/**
 * Height of the bar pinned to the top of the window (the sticky MegaMenu), shared with other
 * sticky elements so they sit below it. CSS reads the custom property; components that need
 * the number in script (an IntersectionObserver margin) read it here and listen for the event.
 */
export const STICKY_OFFSET_PROPERTY = '--poly-space-sticky-topOffset';

/** Dispatched on `window` whenever the published offset changes; `detail` is the height in px. */
export const STICKY_OFFSET_EVENT = 'poly:sticky-offset-change';

/** Publishes the pinned bar's height, or clears it (0) when nothing is pinned. */
export function publishStickyOffset(height: number): void {
  const root = document.documentElement;
  if (height > 0) {
    root.style.setProperty(STICKY_OFFSET_PROPERTY, `${height}px`);
  } else {
    root.style.removeProperty(STICKY_OFFSET_PROPERTY);
  }
  window.dispatchEvent(new CustomEvent<number>(STICKY_OFFSET_EVENT, { detail: Math.max(height, 0) }));
}

/** The currently published offset in px, 0 when nothing is pinned. */
export function readStickyOffset(): number {
  if (typeof document === 'undefined') return 0;
  return parseFloat(document.documentElement.style.getPropertyValue(STICKY_OFFSET_PROPERTY)) || 0;
}

/** Calls `handler` with the new offset in px whenever it changes. Returns the unsubscribe function. */
export function onStickyOffsetChange(handler: (offset: number) => void): () => void {
  const listener = (event: Event) => handler((event as CustomEvent<number>).detail);
  window.addEventListener(STICKY_OFFSET_EVENT, listener);
  return () => window.removeEventListener(STICKY_OFFSET_EVENT, listener);
}
